import { useState, useEffect, FC } from 'react';
import Spinner from '../Spinner';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import ModalAddress from './ModalAddress';
import { PermissionsGate } from '../authorization/PermissionsGate';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import AddIcon from '@mui/icons-material/Add';
import intl from 'components/i18n/ReactIntlWrapper';
import { handleAPIError } from '../../common/errorHandler';
import { Box, Typography, Button, Alert } from '@mui/material';
import { useDialog } from 'common/DialogProvider';
import { getUserEntity } from 'common/UserEntityProvider';
import { DEFAULT_PAGINATION } from 'hooks/useSearch';
import { DataGrid, GridColDef, GridInitialState, GridSortModel } from '@mui/x-data-grid';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { usePaginationPreference } from 'hooks/usePaginationPreference';

interface AddressCompactProps {
  disabled: boolean;
  addressesId: number[];
  personaExist?: boolean;
  setAddressesId: Function;
  editScopes?: string[];
  deleteScopes?: string[];
  identifier?: string;
  allowOne?: boolean;
  inputProvider?: any;
  setInputProvider?: (value: any) => void;
  legalAddress?: boolean;
  realAddress?: boolean;
  countries?: any[] | null;
}

const AddressCompact: FC<AddressCompactProps> = (props) => {
  const {
    addressesId,
    setAddressesId,
    editScopes,
    deleteScopes,
    identifier,
    disabled,
    allowOne = false,
    inputProvider = null,
    setInputProvider = null,
    legalAddress = false,
    realAddress = false,
    countries,
  } = props;

  const api: Api = useApi();

  const [addresses, setAddresses] = useState([]);

  const [adressExist, setAddressExist] = useState(false);

  const toast = useToastMessageQueue();

  const [isAddressDetailOpen, setIsAddressDetailOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDeletingLoading, setIsDeletingLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [addressId, setAddressId] = useState(null);
  const entityType = getUserEntity();
  const [pagination, setPagination] = usePaginationPreference('addressCompact', 10);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'contactName', sort: 'asc' },
  ]);
  const { openDialog, closeDialog } = useDialog();

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
  };

  const fetchAddresses = async (addressesId: number[]) => {
    setLoading(true);
    const response = await api.addressApi
      .apiVversionAddressAddressesGet('1', addressesId, {})
      .then((response) => {
        if (response.data) {
          setAddresses(response.data?.data);
          setLoading(false);
        }
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
        setLoading(false);
      });
  };

  useEffect(() => {
    if (addressesId.length > 0 && addressesId !== null && addressesId[0] !== null) {
      fetchAddresses(addressesId);
    }
  }, [addressesId]);

  const handleEdit = (selectedAddressId) => {
    setAddressId(selectedAddressId);
    setIsAddressDetailOpen(true);
  };

  const handleDelete = (selectedAddressId) => {
    const id = openDialog({
      title: intl.get('delete.modal.title'),
      message: intl.get('delete.modal.addressRow.message'),
      type: 'confirm',
      loading: isDeletingLoading,
      confirmButton: {
        confirmLabel: intl.get('delete.modal.delete.button'),
        handleConfirm: async () => {
          setLoading(true);
          try {
            await api.addressApi
              .apiVversionAddressAddressIdDelete(selectedAddressId, '1', {})
              .then((response) => {
                console.log('address deleted: ' + response);
                let newAddresses = addresses.filter((address) => address.id != selectedAddressId);
                setAddresses(newAddresses);
                // Update addressesId array
                let newAddressesId = addressesId.filter((id) => id != selectedAddressId);
                setAddressesId(newAddressesId);
                toast.success({
                  message: intl.get('addressRow.toast.success.handleDelete'),
                });
                closeDialog(id);
                setInputProvider({...inputProvider, address: null});
              })
              .catch((error) => {
                handleAPIError(error, toast, errors);
                setErrors({ ...errors });
                closeDialog(id); // Close dialog even on error
              });
          } catch (error) {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
            closeDialog(id); // Close dialog on unexpected error
          } finally {
            setLoading(false);
          }
        },
      },
      cancelButton: {
        cancelLabel: intl.get('delete.modal.cancel.button'),
        handleCancel: () => {
          closeDialog(id);
        },
      },
    });
  };

  useEffect(() => {
    addresses.length > 0 ? setAddressExist(true) : setAddressExist(false);
  }, [addresses]);

  const closeAddressDetail = () => setIsAddressDetailOpen(false);

  const handleAddressDetailOpen = () => {
    setIsAddressDetailOpen(true);
  };

  const INITIAL_STATE: GridInitialState = {
    pagination: {
      paginationModel: {
        page: DEFAULT_PAGINATION.pageNum,
        pageSize: DEFAULT_PAGINATION.pageSize,
      },
    },
    sorting: {
      sortModel: [
        {
          field: 'Adress1',
          sort: 'asc',
        },
      ],
    },
  };

  const columns: GridColDef[] = [
    {
      field: 'address1',
      headerName: intl.get('addressCompact.table.thead.address1'),
      width: 250,
      flex: 1,
    },
    {
      field: 'address2',
      headerName: intl.get('addressCompact.table.thead.address2'),
      width: 250,
      flex: 1,
    },
    {
      field: 'country',
      headerName: intl.get('addressCompact.table.thead.country'),
      width: 250,
      flex: 1,
      renderCell: (params) => <>{intl.get(params.row.country?.labelKey) || ''}</>,
    },
    {
      field: 'stateProvince',
      headerName: intl.get('addressCompact.table.thead.stateProvince'),
      width: 250,
      flex: 1,
      renderCell: (params) => <>{params.row.stateProvince.name || ''}</>,
    },
    {
      field: 'city',
      headerName: intl.get('addressCompact.table.thead.city'),
      width: 250,
      flex: 1,
    },
    {
      field: 'zipCode',
      headerName: intl.get('addressCompact.table.thead.zipCode'),
      width: 250,
      flex: 1,
    },
    {
      field: 'actions',
      headerName: '',
      width: 150,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <DefaultActions
            row={params.row}
            entityType={entityType.entityType}
            onEdit={() => handleEdit(params?.row?.id)}
            identifier={identifier}
            editScopes={identifier ? undefined : editScopes}
            disabled={disabled || loading}
          />
          <DefaultActions
            row={params.row}
            entityType={entityType.entityType}
            onDelete={() => handleDelete(params?.row?.id)}
            identifier={identifier}
            deleteScopes={identifier ? undefined : deleteScopes}
            disabled={disabled || loading}
          />
        </Box>
      ),
    },
  ];

  const realOrLegal = legalAddress ? 'Legal' : 'Real';

  return (
    <Box>
      <Typography variant="h6" color="primary" display={'flex'} alignItems="center" gap={1}>
        <MailOutlineIcon />
        {legalAddress || realAddress
          ? `${realOrLegal} ${intl.get('addressCompact.header')}`
          : intl.get('addressCompact.header')}
      </Typography>
      {addresses.length < 1 && (
        <Alert severity="warning" sx={{ mx: 2, my: 1 }}>
          {intl.get('addBilling.alert')}
        </Alert>
      )}
      {loading ? (
        <Box
          style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          <Spinner small={true} />
        </Box>
      ) : (
        <Box>
          <Box sx={{ overflowX: 'auto' }}>
            {addresses && addresses.length > 0 ? (
              <Box style={{ width: '100%', display: 'flex' }}>
                <DataGrid
                  rows={addresses || []}
                  columns={columns}
                  loading={loading}
                  initialState={INITIAL_STATE}
                  rowCount={addresses?.length}
                  paginationModel={pagination}
                  onPaginationModelChange={setPagination}
                  sortModel={sortModel}
                  onSortModelChange={handleSortModelChange}
                  pageSizeOptions={[5, 10, 20]}
                />
              </Box>
            ) : (
              <Box style={{ padding: '20px' }}></Box>
            )}
          </Box>

          <PermissionsGate
            identifier={identifier}
            viewScopes={identifier ? undefined : editScopes}
            editScopes={identifier ? undefined : editScopes}
            viewRoles={[]}
            editRoles={[]}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={disabled || (allowOne && adressExist)}
                onClick={handleAddressDetailOpen}
              >
                {intl.get('addressCompact.newAddress.button')}
              </Button>
              <ModalAddress
                isOpen={isAddressDetailOpen}
                closeModal={closeAddressDetail}
                addressId={addressId}
                addresses={addresses}
                setAddresses={setAddresses}
                addressesId={addressesId}
                setAddressesId={setAddressesId}
                inputProvider={inputProvider}
                setInputProvider={setInputProvider}
                legalAddress={legalAddress}
                realAddress={realAddress}
                countriesParent={countries}
              />
            </Box>
          </PermissionsGate>
        </Box>
      )}
    </Box>
  );
};

export default AddressCompact;
